import { FC, useEffect, useRef, useState } from 'react';
import { useTranslate } from 'hooks/useTranslate';

import { IncentiveOfferType } from 'modules/incentivesMarket/types';
import Page from 'modules/_shared/components/Pages/Page';
import CustomLoading from 'modules/_shared/components/CustomLoading';
import IncentiveOfferEligiblesTable from './components/IncentiveOfferEligiblesTable';

import { useIncentiveOfferDetail } from './context/IncentiveOfferDetailContext';
import IncentiveOfferDetailHeader from './components/IncentiveOfferDetailHeader';
import IncentiveOfferStatusBanner from './components/IncentiveOfferStatusBanner';
import IncentiveOfferDetailSummaryCard from './components/IncentiveOfferDetailSummaryCard';
import IncentiveOfferPriceContextCard from './components/IncentiveOfferPriceContextCard';
import IncentiveOfferAcceptanceProgress from './components/IncentiveOfferAcceptanceProgress';
import IncentiveOfferOperationsTable from './components/IncentiveOfferOperationsTable';
import IncentiveOfferTimeline from './components/IncentiveOfferTimeline';
import IncentiveOfferPublisherInfoCard from './components/IncentiveOfferSellerInfoCard';
import IncentiveOfferActionCard from './components/IncentiveOfferActionCard';
import IncentiveOfferSellerLimitsBanner from './components/IncentiveOfferSellerLimitsBanner';

const IncentiveOfferDetail: FC = () => {
  const { t } = useTranslate();
  const { data, actions } = useIncentiveOfferDetail();

  const summaryRef = useRef<HTMLDivElement>(null);
  const [summaryHeight, setSummaryHeight] = useState<number | undefined>();

  const showSellerLimitsBanner =
    data.offer?.type === IncentiveOfferType.BUY &&
    data.offer?.sellerParticipationLimits &&
    data.offer.sellerParticipationLimits.allowSellAll === false;

  useEffect(() => {
    const element = summaryRef.current;

    if (!element || !showSellerLimitsBanner) {
      setSummaryHeight(undefined);
      return undefined;
    }

    const updateHeight = () => {
      setSummaryHeight(element.offsetHeight);
    };

    updateHeight();

    const resizeObserver = new ResizeObserver(updateHeight);
    resizeObserver.observe(element);

    return () => resizeObserver.disconnect();
  }, [
    data.offer,
    data.availableShares,
    data.isBeneficiaryView,
    data.daysRemaining,
    showSellerLimitsBanner,
  ]);

  if (data.isLoading) {
    return <CustomLoading />;
  }

  if (!data.offer) {
    return (
      <Page id="trading-offer-detail-page">
        <div className="nk-block text-center py-5">
          <em className="icon ni ni-alert-circle fs-1 text-warning mb-3 d-block" />
          <h5>{t('OfferNotFound')}</h5>
          <p className="text-soft">{t('OfferNotFoundDescription')}</p>
          <button
            type="button"
            className="btn btn-primary mt-2"
            onClick={actions.handleGoBack}
          >
            {t('Back')}
          </button>
        </div>
      </Page>
    );
  }

  return (
    <Page id="trading-offer-detail-page">
      <IncentiveOfferDetailHeader
        offer={data.offer}
        offerReference={data.offerReference}
        offerStatus={data.offerStatus}
        societyId={data.societyId}
        societyName={data.societyName}
        isAdmin={data.isAdmin}
        isBeneficiaryView={data.isBeneficiaryView}
        viewingBeneficiary={data.viewingBeneficiary}
        userBeneficiaryIds={data.userBeneficiaryIds}
        beneficiaryQueryParam={data.beneficiaryQueryParam}
      />

      <div className="nk-block">
        {!data.isBeneficiaryView && (
          <IncentiveOfferStatusBanner
            offer={data.offer}
            offerStatus={data.offerStatus}
          />
        )}

        <div className="row g-gs">
          <div
            className={showSellerLimitsBanner ? 'col-lg-7' : 'col-12'}
            ref={summaryRef}
          >
            <IncentiveOfferDetailSummaryCard
              offer={data.offer}
              offerReference={data.offerReference}
              availableShares={data.availableShares}
              daysRemaining={data.daysRemaining}
              isBeneficiaryView={data.isBeneficiaryView}
              beneficiaryMaxTotalSellAmount={
                data.sellerOfferCap?.maxTotalSellAmount
              }
            />
          </div>

          {showSellerLimitsBanner && (
            <div className="col-lg-5">
              <div
                className="overflow-y-auto"
                style={
                  summaryHeight !== undefined
                    ? { maxHeight: summaryHeight }
                    : undefined
                }
              >
                <IncentiveOfferSellerLimitsBanner
                  offer={data.offer}
                  maxTotalSellAmount={data.sellerOfferCap?.maxTotalSellAmount}
                  maxSellableUnits={data.sellerOfferCap?.maxUnitsSellerCanOffer}
                  sellerPlans={data.sellerPlans}
                />
              </div>
              {!data.isBeneficiaryView && (
                <IncentiveOfferAcceptanceProgress
                  offer={data.offer}
                  acceptanceHistory={data.acceptanceHistory}
                  operations={data.incentiveOfferOperations}
                />
              )}
            </div>
          )}
        </div>

        {data.isAdmin && !data.isBeneficiaryView && (
          <div className="row g-gs">
            <div className="col-12">
              <IncentiveOfferEligiblesTable
                eligibles={data.eligibles}
                page={data.eligiblesPage}
                rowsPerPage={data.eligiblesRowsPerPage}
                totalRows={data.eligiblesTotal}
                isLoading={data.isLoadingEligibles}
                isDownloading={data.isDownloadingEligibles}
                onPageChange={actions.handleEligiblesPageChange}
                onRowsPerPageChange={actions.handleEligiblesRowsPerPageChange}
                onSearch={actions.handleEligiblesSearchChange}
                onDownload={actions.handleDownloadEligibles}
              />
            </div>

            <div className="col-12">
              {!data.isBeneficiaryView && (
                <IncentiveOfferOperationsTable
                  operations={data.incentiveOfferOperations}
                  offerType={data.offer.type}
                  onGoToOperation={actions.handleGoToOperation}
                />
              )}
            </div>
          </div>
        )}

        <div className="row g-gs">
          <div className="col-12">
            <IncentiveOfferActionCard
              offer={data.offer}
              availableShares={data.availableShares}
              sellerOfferCap={data.sellerOfferCap}
              sellerPlans={data.sellerPlans}
              purchaseQuantity={data.purchaseQuantity}
              isExpired={data.isExpired}
              isOwnOffer={data.isOwnOffer}
              hasPartnerBuyer={data.hasPartnerBuyer}
              isAdmin={data.isAdmin}
              isBeneficiaryView={data.isBeneficiaryView}
              onQuantityChange={actions.handlePurchaseQuantityChange}
              onSellUnitsConfirm={actions.handleSellUnitsConfirm}
              onBuyShares={actions.handleBuyShares}
              onViewOtherOffers={actions.handleViewOtherOffers}
            />
          </div>
        </div>

        <div className="row g-gs">
          <div className="col-lg-7">
            {/* {!data.isBeneficiaryView && (
              <IncentiveOfferPriceContextCard
                priceContext={data.priceContext}
              />
            )} */}

            {!data.isBeneficiaryView && (
              <IncentiveOfferTimeline steps={data.timelineSteps} />
            )}

            {data.sellerInfo &&
              (!data.isBeneficiaryView ||
                data.offer.type === IncentiveOfferType.SELL) && (
                <IncentiveOfferPublisherInfoCard
                  sellerInfo={data.sellerInfo}
                  offerType={data.offer.type}
                />
              )}
          </div>
        </div>
      </div>

      {data.isLoading && <CustomLoading />}
    </Page>
  );
};

export default IncentiveOfferDetail;
